"use client";

import { Box, Theme, useMediaQuery } from "@mui/material";
import { generateMediaSrc } from "@v2/components/MediaV2";
import Image, { ImageLoaderProps } from "next/image";
import Link from "next/link";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWidthBudget } from "./widthBudget";

// theme.breakpoints: tablet starts at sm2, desktop at lg.
const TABLET_MIN = 768;
const DESKTOP_MIN = 1200;

type ImageLCDataType = {
    media_id: string | null;
    alt: string | null;
    link: string | null;
    priority: boolean;
    width?: string | null;
    ratio?: string | null;
    object_fit?: "cover" | "contain";
    radius?: string | null;
}

type ViewportSettings = {
    width: string | null;
    ratio: string | null;
    object_fit: "cover" | "contain";
    radius: string | null;
}

type ImageLCSettingsType = {
    label: string | null;
    mobile?: ViewportSettings;
    tablet?: ViewportSettings;
    desktop?: ViewportSettings;
}

const ImageLC = memo(function ImageLC({ content, disableLinks }: {
    content: {
        data: ImageLCDataType;
        settings: ImageLCSettingsType;
    };
    disableLinks?: boolean;
}) {
    const { data, settings } = content;
    const containerRef = useRef<HTMLDivElement>(null);
    // The width the cell around this image hands it, per breakpoint.
    const budget = useWidthBudget();
    const [containClipPath, setContainClipPath] = useState<string | undefined>(undefined);
    const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between('sm2', 'lg'));
    const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    // Every viewport is resolved up front so the box can be sized with breakpoints
    // instead of a media query hook. The hook only ever runs on the client, so sizing
    // from it made the server render the mobile box and the browser resize it after
    // hydration.
    const viewports = useMemo(() => {
        const legacySettings: ViewportSettings = {
            width: data.width ?? null,
            ratio: data.ratio ?? null,
            radius: data.radius ?? null,
            object_fit: data.object_fit ?? 'contain',
        };

        return {
            mobile: { ...legacySettings, ...settings.mobile },
            tablet: { ...legacySettings, ...settings.tablet },
            desktop: { ...legacySettings, ...settings.desktop },
        };
    }, [data.object_fit, data.radius, data.ratio, data.width, settings.desktop, settings.mobile, settings.tablet]);

    // The current viewport is still needed for the things CSS cannot pick: the src of a
    // single img tag, and the measured clip path.
    const viewport = isDesktop ? viewports.desktop : isTablet ? viewports.tablet : viewports.mobile;

    const by = useCallback(<T, >(pick: (settings: ViewportSettings) => T) => ({
        xs: pick(viewports.mobile),
        sm2: pick(viewports.tablet),
        lg: pick(viewports.desktop),
    }), [viewports]);

    const radius = useMemo(() => viewport.radius ? `${viewport.radius}px` : undefined, [viewport.radius]);

    // What the browser needs is the width this image is *drawn* at, per breakpoint, so
    // it can pick a source before layout. A fixed `width` setting states it outright;
    // a fluid image is as wide as the cell holding it. This cannot come from the
    // viewport hook: that only resolves on the client, so the server would describe
    // every image as a mobile one and the browser would fetch twice.
    const sizes = useMemo(() => {
        const slot = (v: ViewportSettings, fallback: number) => {
            const fixed = Number(v.width);
            return Math.round(Number.isFinite(fixed) && fixed > 0 ? fixed : fallback);
        };

        return [
            `(max-width: ${TABLET_MIN - 1}px) ${slot(viewports.mobile, budget.xs)}px`,
            `(max-width: ${DESKTOP_MIN - 1}px) ${slot(viewports.tablet, budget.sm2)}px`,
            `${slot(viewports.desktop, budget.lg)}px`,
        ].join(', ');
    }, [budget.lg, budget.sm2, budget.xs, viewports]);

    // next/image asks the loader for each candidate width in its srcset; the media
    // server clamps anything wider than the original, so the cap only keeps the
    // request list short.
    const loader = useCallback(({ width }: ImageLoaderProps) => generateMediaSrc(data.media_id ?? "", {
        width: Math.min(width, 1920),
        quality: 88,
    }), [data.media_id]);

    const src = useMemo(() => data.media_id ? generateMediaSrc(data.media_id) : "", [data.media_id]);
    const isCover = viewport.object_fit === 'cover';

    const updateContainClipPath = useCallback((loadedImage?: HTMLImageElement) => {
        if (isCover || !radius) {
            setContainClipPath(undefined);
            return;
        }

        const container = containerRef.current;
        if (!container) return;
        const img = loadedImage ?? container.querySelector('img');
        if (!img) return;

        const { naturalWidth, naturalHeight } = img;
        if (!naturalWidth || !naturalHeight) return;

        const { width: cw, height: ch } = container.getBoundingClientRect();
        if (!cw || !ch) return;

        const naturalRatio = naturalWidth / naturalHeight;
        const containerRatio = cw / ch;

        let inset = '0 0 0 0';
        if (naturalRatio > containerRatio + 0.01) {
            const pad = Math.max(0, (ch - cw / naturalRatio) / 2);
            inset = `${pad}px 0 ${pad}px 0`;
        } else if (naturalRatio < containerRatio - 0.01) {
            const pad = Math.max(0, (cw - ch * naturalRatio) / 2);
            inset = `0 ${pad}px 0 ${pad}px`;
        }

        setContainClipPath(`inset(${inset} round ${radius})`);
    }, [isCover, radius]);

    const handleLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
        updateContainClipPath(event.currentTarget);
    }, [updateContainClipPath]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => updateContainClipPath());
        observer.observe(container);
        updateContainClipPath();

        return () => observer.disconnect();
    }, [updateContainClipPath, viewport.ratio, viewport.width]);

    if (!data?.media_id) return null;

    const image = (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                aspectRatio: by(v => v.ratio || undefined),
                borderRadius: by(v => v.object_fit === 'cover' && v.radius ? `${v.radius}px` : undefined),
                overflow: by(v => v.object_fit === 'cover' ? 'hidden' : 'visible'),
                position: 'relative',
                // objectFit has to live here rather than on the img style prop, because an
                // inline style cannot vary per breakpoint.
                '& img': { objectFit: by(v => v.object_fit) },
            }}
        >
            <Image
                src={src}
                loader={loader}
                sizes={sizes}
                alt={data.alt || ""}
                fill
                priority={data.priority}
                loading={data.priority ? 'eager' : 'lazy'}
                fetchPriority={data.priority ? 'high' : 'low'}
                onLoad={handleLoad}
                style={{
                    width: '100%',
                    height: '100%',
                    clipPath: !isCover ? containClipPath : undefined,
                }}
            />
        </Box>
    );

    return (
        <Box sx={{
            width: by(v => v.width ? `${v.width}px` : '100%'),
            mx: by(v => v.width ? 'auto' : undefined),
            userSelect: 'none',
        }}>
            { data.link && !disableLinks ? (
                <Link href={data.link} style={{ display: 'block', width: '100%' }}>
                    {image}
                </Link>
            ) : image}
        </Box>
    );
});

export default ImageLC;
